import { useMemo } from "react";
import { ArrowRight, Calendar, User } from "lucide-react";
import { ASSESSMENT_STEP_NAMES, VERSION_STATUS_STYLES } from "../../constants";
import { formatDate } from "../../utils/formatters";
import type { Assessment, VersionStatus, AssessmentStatus } from "../../types";
import { cn } from "@nextui-org/react";

interface VersionCardProps {
  assessment: Assessment;
  onNavigate: (assessment: Assessment) => void;
  memberIdNameMapping?: Record<string, string>;
}

/**
 * Get step name based on assessment step and status
 */
function getStepName(step: number, status: AssessmentStatus): string {
  if (status === 'complete') return 'Completed';
  return ASSESSMENT_STEP_NAMES[step] || `Step ${step}`;
}

export function VersionCard({ assessment, onNavigate, memberIdNameMapping }: VersionCardProps) {
  // Derive version status
  const status: VersionStatus = assessment.is_active
    ? 'active'
    : assessment.status === 'complete'
    ? 'completed'
    : 'archived';

  // Derive date display
  const dateInfo = useMemo(() => {
    if (assessment.is_active) {
      return { label: 'Updated', date: assessment.updated_at };
    }
    if (assessment.status === 'complete') {
      return { label: 'Completed', date: assessment.updated_at };
    }
    return { label: 'Archived', date: assessment.archived_at };
  }, [assessment]);

  // Get user name from ID using memberIdNameMapping
  const userId = assessment.is_active
    ? assessment.last_reassessed_by || assessment.created_by
    : assessment.archived_by || assessment.created_by;

  const userName = useMemo(() => {
    if (!userId) return 'Unknown';
    if (memberIdNameMapping && memberIdNameMapping[userId]) {
      return memberIdNameMapping[userId];
    }
    return 'Unknown';
  }, [userId, memberIdNameMapping]);

  // Get step name based on assessment_step
  const stepName = getStepName(assessment.assessment_step, assessment.status);

  // Calculate progress
  const progress = Math.min((assessment.assessment_step / assessment.total_assessment_steps) * 100, 100);

  // Get status badge styles
  const statusStyle = VERSION_STATUS_STYLES[status];

  // Disable navigation when assessment is at step 3 (generating results)
  const isNavigationDisabled = assessment.assessment_step === 3;

  return (
    <div className="flex flex-col items-start p-4 gap-4 bg-[#F8FAFC] rounded-lg">
      {/* Top Row: Version + Status Badge + Arrow */}
      <div className="flex flex-row items-center gap-3 w-full h-5">
        <div className="flex flex-row items-center gap-3 flex-1">
          <span className="font-['Inter'] font-medium text-sm leading-5 text-[#314158]">
            v{assessment.version}
          </span>
          <span className={cn(
            'flex items-center px-[7px] py-0.5 rounded-md font-["Inter"] font-normal text-xs leading-4',
            statusStyle.bg,
            statusStyle.text
          )}>
            {statusStyle.label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => !isNavigationDisabled && onNavigate(assessment)}
          disabled={isNavigationDisabled}
          className={cn(
            "w-5 h-5 flex items-center justify-center transition-opacity",
            isNavigationDisabled
              ? "cursor-not-allowed opacity-40"
              : "hover:opacity-70"
          )}
          aria-label={`View version ${assessment.version}`}
        >
          <ArrowRight className={cn(
            "w-5 h-5",
            isNavigationDisabled ? "text-[#A1A1AA]" : "text-[#006FEE]"
          )} strokeWidth={1.5} />
        </button>
      </div>

      {/* Middle Row: Date + User Info */}
      <div className="flex flex-row items-center gap-4 w-full">
        <div className="flex flex-row items-center gap-1.5 flex-1">
          <Calendar className="w-3 h-3 text-[#45556C]" />
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#45556C]">
            {dateInfo.label} {formatDate(dateInfo.date)}
          </span>
        </div>
        <div className="flex flex-row items-center gap-1.5 flex-1">
          <User className="w-3 h-3 text-[#45556C]" />
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#45556C]">
            {userName}
          </span>
        </div>
      </div>

      {/* Bottom Row: Step Name + Progress */}
      <div className="flex flex-col items-start gap-1 w-full">
        <div className="flex flex-row items-start gap-1 w-full">
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#62748E] flex-1">
            {stepName}
          </span>
          <span className="font-['Inter'] font-normal text-xs leading-4 text-[#62748E]">
            Step {assessment.assessment_step} of {assessment.total_assessment_steps}
          </span>
        </div>
        <div className="h-1 bg-[#E2E8F0] rounded-full overflow-hidden w-full">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              status === 'completed' ? 'bg-[#006FEE]' : 'bg-[#006FEE]'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
